import React, { useMemo, useState } from 'react';
import { Tooltip } from 'antd';
import moment from 'moment-timezone';
import { resolveCampaignDateRange } from '../../../../../../../core/utils/campaignDateRange';
import { Calendar, UserPlus, ArrowRight } from 'lucide-react';

import CampaignRangeSelector from '../../../components/CampaignRangeSelector';
import SignUpsV2Chart from './SignUpsV2Chart';
import PerformanceCardSkeleton from '../../../components/PerformanceCardSkeleton';
import { formatWholeNumber } from '../../../../../../../core/utils/campaigns';
import { useTimezone } from '../../../../../../../core/hooks/useTimezone';
import KpiBar, { KpiCard } from '../../../../../../shared/designSystem/components/KpiBar';
import { formatDateOnly } from '../../../../../../shared/designSystem/utils/dateFormat';

// =============================================================================
// SignUpsPerformance — unified Performance card for the V2 SignUps tab.
//
// One section wrapping: header (eyebrow + range selector), 2-column KPI bar
// (Flight Dates + Sign Ups total), trend chart. Mirrors the Summary /
// Display / Transactions Performance pattern so all tabs read the same.
//
// KPI bar columns: Flight Dates (with progress bar) + Sign Ups total. Only
// 2 cards because sign-ups don't carry spend / revenue / attribution-count
// distinctions at the campaign-summary level.
//
// Loading pattern — swap the whole section for `<PerformanceCardSkeleton/>`
// when `chartLoading` is true. Matches the unified "shimmer-per-card"
// loading vocabulary every non-Overview tab uses.
// =============================================================================

const flightProgress = (startDate, endDate) => {
  const total = moment(endDate).diff(moment(startDate), 'days');
  const progress = moment().diff(moment(startDate), 'days');
  if (total <= 0 || progress <= 0) return 0;
  if (progress >= total) return 100;
  return Math.floor((progress / total) * 100);
};

const SignUpsPerformance = ({
  startDate,
  endDate,
  totalSignups = 0,
  signupsByDateData,
  chartLoading = false,
  dateRange,
  onDateRangeChange,
  campaignOrder,
}) => {
  const { timezone: effectiveTimezone = 'UTC' } = useTimezone();
  const [rangeType, setRangeType] = useState('Custom');

  // Default range for the Custom-pill label — derived from the data's own
  // min/max if available, else the campaign flight dates.
  // Campaign flight start → today (see core/utils/campaignDateRange). Also
  // fixes a tz shift: the old flight fallback read UTC-stored dates in the
  // viewer's zone, moving the window a day west of UTC.
  const defaultDateRange = useMemo(() => {
    return resolveCampaignDateRange(
      campaignOrder,
      (signupsByDateData || []).map((d) => d && d.date)
    );
  }, [signupsByDateData, campaignOrder]);

  const progressPercent = flightProgress(startDate, endDate);

  if (chartLoading) return <PerformanceCardSkeleton />;

  return (
    <section className="campaign-overview__performance">
      <header className="campaign-overview__performance-head">
        <div className="campaign-overview__performance-eyebrow mono-eyebrow">
          Performance
        </div>
        <CampaignRangeSelector
          rangeType={rangeType}
          byCampaignRangeDate={dateRange}
          setRangeType={setRangeType}
          setByCampaignRangeDate={(newRange) => {
            if (onDateRangeChange) onDateRangeChange(newRange);
          }}
          timezone={effectiveTimezone}
          defaultDateRange={defaultDateRange}
        />
      </header>

      <KpiBar>
        {/* Flight-dates card — custom body (date pair + progress bar + %
            elapsed). Rendered as a raw BEM block since its body shape is
            campaign-specific and doesn't fit the KpiCard value slot. */}
        <div className="campaign-overview__kpi">
          <div className="campaign-overview__kpi-head">
            <span className="campaign-overview__kpi-icon campaign-overview__kpi-icon--neutral">
              <Calendar size={16} />
            </span>
            <Tooltip
              overlayClassName="mjl-ds"
              title="Flight Dates refers to the Start & End Dates of the individual campaign"
            >
              <span className="campaign-overview__kpi-eyebrow">Flight Dates</span>
            </Tooltip>
          </div>
          <div className="campaign-overview__flight">
            <div className="campaign-overview__flight-dates">
              <span>{formatDateOnly(startDate, '—')}</span>
              <ArrowRight size={14} className="campaign-overview__flight-arrow" />
              <span>{formatDateOnly(endDate, '—')}</span>
            </div>
            <div className="campaign-overview__flight-progress">
              <div
                className="campaign-overview__flight-progress-bar"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
            <div className="campaign-overview__flight-meta">
              {progressPercent}% elapsed
            </div>
          </div>
          <div className="campaign-overview__kpi-footer" />
        </div>

        <KpiCard
          icon={<UserPlus size={16} />}
          iconTint="emerald"
          label="Sign Ups"
          value={formatWholeNumber(totalSignups)}
        />
      </KpiBar>

      <SignUpsV2Chart data={signupsByDateData || []} />
    </section>
  );
};

export default SignUpsPerformance;
